import { ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, HostBinding, Input, Optional, Output, SkipSelf, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatMenu } from '@angular/material/menu';
import { MatTabGroup } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import {
  buildDemoReportGroups,
  isDemoReportGroupId,
  isDemoReportUserId,
  selectionContainsDemoIds
} from 'src/app/services/demo/demo-report-users.mock';
import { DemoService } from 'src/app/services/demo/demo.service';
import { ReportLazyUserService } from 'src/app/services/lazy/user/lazy-users.service';
import { AmpTrackCtx } from 'src/app/services/tracking/amp-track';
import { AuthState } from 'src/app/store/auth/auth.state';
import { ManagerState } from 'src/app/store/manager/manager.state';
import { advancedStringFilter, ValueAccessorBase } from 'src/app/util';
import { AuthCompany, AuthUser, Capture2Tracking, User, UserGroup } from 'src/models';
import { SearchComponent } from '../search/search.component';
import { LazyUserSelection } from './lazy-user-selection';

export * from './lazy-user-selection';


@UntilDestroy()
@Component({
  selector: 'app-user-filter-lazy',
  templateUrl: './index.html',
  styleUrls: ['./index.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserFilterLazyComponent),
      multi: true,
    },
  ],
})
export class UserFilterLazyComponent extends ValueAccessorBase<LazyUserSelection> {
  @ViewChild(SearchComponent, { static: false }) searchComponent: SearchComponent;
  readonly membersOfNoGroupId = LazyUserSelection.membersOfNoGroupId;
  @Input() fireChangeOnClose = true;
  @Input() showSingleUser = false;
  @Input() showUsers = true;
  @Input() showGroups = true;
  @Input() showMembersOfNoGroup = false;
  @Input() showMembersOfMultipleGroups = false;
  @Input() showArchivedUsers = true;
  @Input() noGroupsTranslate = 'common.noGroupsAvailableAndAsk';
  @Input() disabledForUpsell = false;
  @Input() hideManagerFilter = false;
  @Input() placeholderKey: string;
  @Input() disableForSCI = false;
  @Input() showAdcFilter = false;
  @Input() capture2Tracking: Capture2Tracking;
  /**
   * Drops this component's own mat-button trigger so a host can supply its own and open `menu`
   * through [matMenuTriggerFor], forwarding (menuOpened)/(menuClosed) to keep selection commits
   * intact. Off by default, so every existing consumer renders exactly as before.
   */
  @Input() hideDefaultTrigger = false;

  /** Exposed for hosts using `hideDefaultTrigger` with their own [matMenuTriggerFor]. */
  @ViewChild(MatMenu, { static: true }) menu: MatMenu;
  @Output() adcOnlyFilterChange = new EventEmitter<boolean>();

  readonly adcOnlyFilter$ = new BehaviorSubject<boolean>(false);

  isFromPayroll$ = new BehaviorSubject<boolean>(false);
  @Input() set isFromPayroll(value: boolean) { this.isFromPayroll$.next(value); }
  get isFromPayroll() { return this.isFromPayroll$.value; }

  alwaysShow$ = new BehaviorSubject<boolean>(true);
  @Input() set alwaysShow(value: boolean) { this.alwaysShow$.next(value); }

  filter$ = new BehaviorSubject('');

  customUsers$ = new BehaviorSubject<User[]>(undefined);
  @Input() set customUsers(value: User[]) { this.customUsers$.next(value); }
  customUsersFiltered$ = combineLatest([this.customUsers$, this.filter$]).pipe(
    map(([customUsers, searchFilter]) => !searchFilter || !customUsers ? customUsers :
      advancedStringFilter(customUsers, searchFilter, x => [x.name, x.email, x.employeeId])));

  @Select(ManagerState.groups) groups$!: Observable<UserGroup[]>;
  users$: Observable<User[]>;
  @Select(AuthState.company) company$!: Observable<AuthCompany>;
  @Select(AuthState.user) authUser$!: Observable<AuthUser>;

  includeArchivedUsers$: Observable<boolean>;
  searchLoading$: Observable<boolean>;

  usersToShow$: Observable<User[]>;
  get userList$() { return this.usersToShow$; }

  groupsToShow$ = combineLatest([
    this.groups$,
    this.authUser$,
    this.company$,
    this.filter$,
    this.demoService.showDemoData$.pipe(startWith(this.demoService.isShowDemoDataActiveSnapshot())),
  ]).pipe(
    map(([groups, user, company, searchFilter, showDemoData]) => {
      let sourceGroups = showDemoData === true
        ? buildDemoReportGroups()
        : (groups || []).filter(group => !isDemoReportGroupId(group?.id));
      sourceGroups = advancedStringFilter(sourceGroups, searchFilter, x => x.name);
      if (showDemoData !== true && ['manager', 'guest'].includes(company?.role)) {
        return sourceGroups.filter(group => group.managers?.includes(user.id));
      }
      return sourceGroups;
    }),
  );

  private allUsersCount: number;
  private allGroups: UserGroup[];

  userVs$ = new BehaviorSubject<CdkVirtualScrollViewport>(null);
  @ViewChild('userVs', { static: false }) set userVs(val: CdkVirtualScrollViewport) { this.userVs$.next(val); }
  get userVs() { return this.userVs$.value; }

  @ViewChild('groupVs', { static: false }) groupVs: CdkVirtualScrollViewport;
  @ViewChild(MatTabGroup, { static: false }) tabGroup: MatTabGroup;
  /** True only between a real tab click/key and the async selectedTabChange it causes. */
  private userTabInteraction = false;

  private tempValue$ = new BehaviorSubject<LazyUserSelection>(null);
  set tempValue(value: LazyUserSelection) {
    if (this.fireChangeOnClose) {
      this.tempValue$?.next(value);
    } else {
      this.value = value;
      this.tempValue$?.next(null);
    }
  }
  get tempValue() { return this.tempValue$?.value || this.value; }

  readonly hasSingleUser$ = this.value$.pipe(
    map(x => x?.type === 'users' && x.users.count === 1 ? x.users.list[0] : null),
    switchMap(x => !x ? of(null) : this.service.resolve(x)),
  );

  hasNonAdcUsers$: Observable<boolean>;

  /**
   * Sub-nav user/group filter identity. Tagged here (not per-page) so every
   * report sub-nav emits `user-group-selector` consistently. Context resolves
   * from the nearest `AmpTrackCtx` (page or app shell).
   */
  @HostBinding('attr.data-amp-track-component') readonly ampTrackComponent = 'user-group-selector';
  @HostBinding('attr.data-amp-track-context') get ampTrackContextAttr() {
    return this.ampTrackCtx?.value ?? null;
  }

  constructor(
    private cd: ChangeDetectorRef,
    private service: ReportLazyUserService,
    private router: Router,
    private demoService: DemoService,
    @Optional() @SkipSelf() private readonly ampTrackCtx: AmpTrackCtx,
  ) {
    super();

    this.value = LazyUserSelection.none;

    this.demoService.showDemoData$.pipe(
      distinctUntilChanged(),
      untilDestroyed(this),
    ).subscribe(showDemoData => {
      this.service.reset();
      if (showDemoData === false) {
        this.replaceDemoCatalogSelection();
        this.refreshVs();
        this.cd.markForCheck();
      }
      this.tempValue$.next(null);
    });

    this.includeArchivedUsers$ = combineLatest([this.tempValue$, this.value$]).pipe(
      untilDestroyed(this),
      map(([tempValue, value]) => tempValue?.includeArchivedUsers || value?.includeArchivedUsers),
      distinctUntilChanged(),
    );

    const allUsersCountCache = new Map<string, number>();
    const allUsersCountCacheKey = (includeArchivedUsers: boolean, adcOnly: boolean, isDemo: boolean) =>
      `${!!includeArchivedUsers}|${!!adcOnly}|${!!isDemo}`;
    combineLatest([
      this.includeArchivedUsers$,
      this.isFromPayroll$,
      this.adcOnlyFilter$,
      this.demoService.showDemoData$.pipe(distinctUntilChanged()),
    ]).pipe(
      untilDestroyed(this),
      filter(() => this.tempValue.type === 'users'),
    ).subscribe(([includeArchivedUsers, isFromPayroll, adcOnly, showDemoData]) => {
        const isDemo = showDemoData === true;
        const cached = allUsersCountCache.get(allUsersCountCacheKey(includeArchivedUsers, adcOnly, isDemo));
        if (cached !== undefined) {
          this.allUsersCount = cached;
          this.patchAllUsersCount(this.allUsersCount);
          return;
        }

        const payrollParam = this.getPayrollApiParamFromValue(isFromPayroll);
        const detailParam = this.getAdcDetailApiParam();
        const adcOnlyParam = this.getAdcOnlyApiParam(adcOnly);
        service.all({
          ...this.getIncludeArchivedUsersApiParam(includeArchivedUsers),
          ...payrollParam,
          ...detailParam,
          ...adcOnlyParam,
        }).metadata().then(x => {
          this.allUsersCount = x.totalCount;
          this.patchAllUsersCount(this.allUsersCount);
          allUsersCountCache.set(allUsersCountCacheKey(includeArchivedUsers, adcOnly, isDemo), this.allUsersCount);
        });
      });


    this.users$ = combineLatest([
      this.userVs$.pipe(
        switchMap(x => x?.renderedRangeStream ?? of(null as ListRange)),
        distinctUntilChanged((a, b) => a?.start === b?.start && a?.end === b?.end),
        map(x => ({ start: x?.start || 0, end: Math.max(x?.end || 0, (x?.start || 0) + 20) })),
        debounceTime(300),
        shareReplay(1)),
      combineLatest([
        this.filter$.pipe(distinctUntilChanged()),
        this.includeArchivedUsers$,
        this.isFromPayroll$,
        this.adcOnlyFilter$,
        // Rebuild the pager when sample data is dismissed; `all()` caches by options only.
        this.demoService.showDemoData$.pipe(distinctUntilChanged()),
      ]).pipe(
        map(([x, includeArchivedUsers, isFromPayroll, adcOnly]) => {
          const payrollParam = this.getPayrollApiParamFromValue(isFromPayroll);
          const detailParam = this.getAdcDetailApiParam();
          const adcOnlyParam = this.getAdcOnlyApiParam(adcOnly);
          const baseParams = {
            ...this.getIncludeArchivedUsersApiParam(includeArchivedUsers),
            ...payrollParam,
            ...detailParam,
            ...adcOnlyParam,
          };
          // Clear the lazy-service cache or mock pages stick after sample data is dismissed.
          if (!x) {
            service.reset();
          }
          return x ? service.search(x, baseParams) : service.all(baseParams);
        }),
        shareReplay(1)),
    ]).pipe(
      switchMap(([range, pager]) => {
        const preallocated = pager.preallocated();
        preallocated.loadRange(range.start, range.end);
        return preallocated.results;
      }),
      shareReplay(),
      untilDestroyed(this),
    );

    this.usersToShow$ = combineLatest([
      this.customUsersFiltered$.pipe(switchMap(x => x ? of(x) : this.users$)),
      this.authUser$,
      this.demoService.showDemoData$.pipe(startWith(this.demoService.isShowDemoDataActiveSnapshot())),
    ]).pipe(
      map(([users, authUser, showDemoData]) => {
        if (!users) {
          return users;
        }
        const list = showDemoData === true
          ? users
          : users.filter(user => !isDemoReportUserId(user?.id));
        if (this.hideManagerFilter && authUser) {
          return list.filter(user => user?.id !== authUser?.id);
        }
        return list;
      }),
    );

    combineLatest([this.groupsToShow$]).pipe(untilDestroyed(this)).subscribe(([groups]) => {
      this.patchAllGroups(groups);
    });

    this.filter$.pipe(untilDestroyed(this)).subscribe(async searchFilter => {
      if (searchFilter && this.tempValue.all) {
        this.masterToggle(false);
        cd.detectChanges();
      }
    });

    this.searchLoading$ = combineLatest([this.filter$, this.users$]).pipe(
      map(([searchFilter, users]) => searchFilter && !users));

    this.hasNonAdcUsers$ = this.usersToShow$.pipe(
      map(users => (users || []).some(u => u && !u.enableCapture2)),
      distinctUntilChanged(),
    );
  }

  getIncludeArchivedUsersApiParam(includeArchivedUsers: boolean): { 'include-archived-users': 1 } | {} {
    return includeArchivedUsers ? { 'include-archived-users': 1 } : {};
  }

  getPayrollApiParamFromValue(isFromPayroll: boolean): { 'from-payroll': 1 } | {} {
    return isFromPayroll ? { 'from-payroll': 1 } : {};
  }

  getAdcDetailApiParam(): { detail: 'settings' } | {} {
    return this.showAdcFilter ? { detail: 'settings' as const } : {};
  }

  getAdcOnlyApiParam(adcOnly: boolean): { adc_enabled: true } | {} {
    return adcOnly ? { adc_enabled: true as const } : {};
  }

  writeValue(value: LazyUserSelection) {
    const incoming = value || LazyUserSelection.none;
    const replacedDemoSelection = this.shouldReplaceDemoCatalogSelection(incoming);
    const resolved = (replacedDemoSelection ? LazyUserSelection.default : incoming)
      .setAllGroups(this.allGroups, this.allGroups?.length + 1)
      .setAllUsers(null, this.allUsersCount);

    super.writeValue(resolved);
    this.tempValue$.next(null);
    this.cd.detectChanges();

    if (replacedDemoSelection) {
      queueMicrotask(() => this.notifyChange(resolved));
    }
  }

  private shouldReplaceDemoCatalogSelection(selection: LazyUserSelection | null | undefined): boolean {
    return !this.demoService.isShowDemoDataActiveSnapshot()
      && selectionContainsDemoIds(selection?.toSerializable());
  }

  private replaceDemoCatalogSelection(): void {
    if (this.shouldReplaceDemoCatalogSelection(this.value)) {
      this.value = LazyUserSelection.default;
    }
  }

  masterToggle(checked) {
    this.tempValue = checked ? this.tempValue.selectAll() : this.tempValue.clear();

    if (checked) {
      this.filter$.next('');
    }

    this.cd.detectChanges();
  }

  setSelected(selected: boolean, item: string) {
    // This makes sure the `All users` checkbox is unselected When changing selection of a group
    if (this.tempValue.type === 'groups' && this.tempValue.all) this.tempValue = this.tempValue.switchGroupsAll();
    this.tempValue = this.tempValue.set(selected, item);
    this.tempValue = this.tempValue.setNoTag(false);
    this.tempValue = this.tempValue.setMultipleTag(false);
  }

  setNoTag(selected: boolean) {
    this.tempValue = this.tempValue.setNoTag(selected);
  }

  setMultipleTag(selected: boolean) {
    this.tempValue = this.tempValue.setMultipleTag(selected);
  }

  setIncludeArchivedUsers(selected: boolean, users?: User[]) {
    this.tempValue = this.tempValue.setIncludeArchivedUsers(selected, users?.filter(x => x?.archived)?.map(x => x.id));
  }

  markUserTabInteraction() {
    this.userTabInteraction = true;
  }

  typeChanged({ index }) {
    const typeBefore = this.tempValue?.type ?? null;
    const nextType = index === 1 ? 'groups' : 'users';
    // MatTabGroup emits selectedTabChange asynchronously, including for programmatic
    // selectedIndex updates. Payroll renders two of these menus, so a stale Groups
    // index from one copy flips the shared selection and the other copy echoes it.
    // Other pages have a single menu and still follow those programmatic updates.
    if ((!this.isFromPayroll || this.userTabInteraction) && typeBefore !== nextType) {
      this.filter$.next('');
      this.tempValue = this.tempValue.setType(nextType);
      this.refreshVs();
    }
    this.userTabInteraction = false;
  }

  menuClosed() {
    if (this.fireChangeOnClose && this.tempValue && this.tempValue !== this.value) {
      this.value = this.tempValue;
      this.tempValue = null;
    }
  }

  /**
   * Group list and user total are catalog metadata. On Payroll they must not go out through
   * ngModel: both dropdown copies start empty, and publishing that empty selection replaces the
   * restored user ids. Other pages still publish so stored filters pick up the group catalog.
   */
  private patchAllGroups(groups: UserGroup[]) {
    this.allGroups = groups;
    this.applyCatalogMetadata(selection => selection.setAllGroups(groups, groups?.length + 1));
  }

  private patchAllUsersCount(count: number) {
    this.allUsersCount = count;
    this.applyCatalogMetadata(selection => selection.setAllUsers(null, count));
  }

  private applyCatalogMetadata(update: (selection: LazyUserSelection) => LazyUserSelection) {
    if (this.isFromPayroll) {
      const pending = this.tempValue$.value;
      if (pending) {
        this.tempValue$.next(update(pending));
      }
      if (this.value) {
        super.writeValue(update(this.value));
        this.cd.markForCheck();
      }
      return;
    }

    this.tempValue = this.value = update(this.tempValue);
  }

  menuOpened() {
    this.filter$.next('');
    this.refreshVs();
    if (this.tabGroup) {
      this.tabGroup.realignInkBar();
    }
    this.searchComponent.focus();
  }

  isAdcUser(user: User): boolean {
    if (this.capture2Tracking === 'on') return true;
    if (this.capture2Tracking === 'user') return !!user?.enableCapture2;
    return false;
  }

  isAdcGroup(group: UserGroup): boolean {
    if (this.capture2Tracking === 'on') return true;
    if (this.capture2Tracking === 'user') return !!group?.enableCapture2;
    return false;
  }

  toggleAdcOnlyFilter(checked: boolean) {
    this.adcOnlyFilter$.next(checked);
    this.adcOnlyFilterChange.emit(checked);
  }

  navigateToManageUser(userId: string) {
    this.router.navigate(['/manage-users'], { queryParams: { userId } });
  }

  navigateToManageGroup(groupId: string) {
    this.router.navigate(['/manage-users'], { queryParams: { groupId } });
  }

  private refreshVs() {
    // HACK: Scroll position is resetted when the tab changes.
    // This is a workaround to fix items not rendering after reset.
    this.userVs?.checkViewportSize();
    this.groupVs?.checkViewportSize();
  }
}
